import { IdentityPropLookup, IdentityPropsConfig } from './StandardCardView.types';
import { TenantConfigViewData } from 'src/dao/tenantConfigClient';
import { isNil } from 'lodash';
import store from 'src/store';
import { STYLE_ID, STYLE_NAME, DESCRIPTION, ID, NAME } from 'src/utils/Domain/Constants';
import { CardViewGroup, CardViewItem } from './UIData.types';
import { StandardCardProps } from './StandardCard/StandardCard';
import { ContainerPayload } from 'src/components/RightContainer/RightContainer.slice';
import { AdornmentType } from 'configuration-schemas/viewdefns/literals';
import { ConfigurableGridButton } from 'configuration-schemas/viewdefns/general';

export function retrieveIdentityPropsConfig(
  config: TenantConfigViewData & { identityProps?: IdentityPropsConfig },
): IdentityPropsConfig | undefined {
  // just in case config makes it here undefined don't destructure it
  if (!isNil(config?.identityProps)) {
    return config?.identityProps;
  }
  const viewItems: Array<{ dataIndex?: string; xtype?: string }> = config?.main?.view ?? [];
  const displayTitle = viewItems.find((v) => v.xtype === 'displayTitle')?.dataIndex;
  const body = viewItems.find((v) => v.xtype === 'body')?.dataIndex;

  if (isNil(store.getState().perspective.activePage)) {
    return undefined;
  }
  // fallback to previously hardcoded values

  // StandardCardView style review views are configured slightly different
  const [, , , section] = store.getState().perspective.activePage.split('/');
  const isStyleReviewSection = section === 'style-review';

  // This feels like it should be done during mapping in CardViewDataProcessor
  // but it's risky to mess with during the CollectionCard demo work.
  if (isStyleReviewSection && config.main?.cardType !== 'CollectionCard') {
    return {
      id: STYLE_ID,
      name: displayTitle ?? NAME,
      description: body ?? DESCRIPTION,
      renderStyleData: true,
      leafId: ID,
    };
  }

  return {
    id: ID,
    name: displayTitle ?? NAME,
    description: body ?? DESCRIPTION,
    renderStyleData: false,
  };
}

/** Mark card items as selected based on selectedIds + selectionDataIndex */
export const markSelectedOnGroups = (
  groups: CardViewGroup[],
  selectedIds: string[],
  selectionDataIndex?: string,
): CardViewGroup[] => {
  // selection disabled for this view
  if (!selectionDataIndex || selectedIds.length === 0) {
    return groups;
  }

  return groups.map((group) => ({
    ...group,
    items: (group.items ?? []).map((item) => {
      const id = (item as any)?.[selectionDataIndex];

      return {
        ...item,
        isSelected: id != null && selectedIds.includes(id),
      };
    }),
  }));
};

export interface GenerateCardProps {
  styleItem: CardViewItem;
  identityPropsConfig: IdentityPropsConfig;
  currentTab?: string;
  adornments: AdornmentType[];
  onItemClick?: (item: ContainerPayload, eventTarget?: HTMLElement) => void;
  onMoreInfoClick?: (item: ContainerPayload) => void;
  popoverTitle?: string;
  isPrintMode?: boolean;
  allowWorklistFunctionality?: boolean;
  selectable?: boolean;
  onSelectItem?: (item: CardViewItem) => void;
  selectionDataIndex?: string;
  /** Added for CollectionCard to support link buttons there */
  buttons?: ConfigurableGridButton[];
}
export const mapIdentityToItemProp = (
  prop: string,
  propLookup: IdentityPropLookup,
  renderStyleData: boolean,
  styleItem: CardViewItem,
) => {
  switch (propLookup) {
    case ID:
      return prop === STYLE_ID ? styleItem.styleId : (styleItem as Record<string, any>)[prop];
    case NAME:
      return prop === STYLE_NAME ? styleItem.styleName : (styleItem as Record<string, any>)[prop];
    case DESCRIPTION:
      return (
        styleItem.description ??
        (renderStyleData ? styleItem.styleDescription : (styleItem as Record<string, any>)[prop])
      );
  }
};
export const generateCardProps = (props: GenerateCardProps): StandardCardProps => {
  const {
    styleItem,
    identityPropsConfig,
    currentTab,
    adornments,
    onItemClick,
    onMoreInfoClick,
    popoverTitle,
    isPrintMode,
    allowWorklistFunctionality,
    selectable,
    onSelectItem,
    selectionDataIndex,
    buttons,
  } = props;
  const { id: idProp, name: nameProp, description: descProp, leafId, renderStyleData } = identityPropsConfig;
  const renderedImg = styleItem.imgSrc;

  const id = mapIdentityToItemProp(idProp, ID, renderStyleData, styleItem);
  const name = mapIdentityToItemProp(nameProp, NAME, renderStyleData, styleItem);
  const description = mapIdentityToItemProp(descProp, DESCRIPTION, renderStyleData, styleItem);

  const selectionId =
    selectionDataIndex && (styleItem as Record<string, any>)[selectionDataIndex]
      ? (styleItem as Record<string, any>)[selectionDataIndex]
      : undefined;

  const cardProps: StandardCardProps = {
    ...styleItem,
    id: id || styleItem.styleId,
    name: name || styleItem.name,
    leafId: !isNil(leafId) ? (styleItem as Record<string, any>)[leafId] : undefined,
    description: description || styleItem.description,
    styleDescription: styleItem.description,
    imgSrc: renderedImg,
    onMoreInfoClick,
    onItemClick,
    popoverTitle: popoverTitle,
    isSelected: styleItem.isSelected || false,
    stylePaneKey: currentTab,
    adornments,
    isPrintMode,
    allowWorklistFunctionality,
    // props for selection
    selectable: selectable,
    onSelectItem,
    selectionId,
    buttons,
  };

  return cardProps;
};
